/**
 * URL attributes on spans and metrics are route templates, never raw paths
 * (spec multi-product 011 §2, R-011-2).
 *
 * Org slugs sit in URLs under `/o/<slug>`, and record ids (UUIDs, numeric
 * keys, Clerk and Stripe ids) sit in API and page paths. A raw path on a span
 * names the firm and the record; on a metric it is also one series per id.
 * `routeTemplateOf` turns each such segment into its template placeholder
 * (`/o/[orgSlug]/clients/[id]`), so "which endpoint" survives and "which firm"
 * does not. The span exporter applies it to every URL attribute
 * (`span-attribute-scrubber.ts`); code that sets a URL attribute or label by
 * hand calls it, and `url-attribute-template-ratchet.test.ts` holds both.
 *
 * Only the path is templated. The scheme and host stay ("which endpoint"),
 * and the query and fragment are left to the scrubber's own rules.
 */

/** Span attribute keys that carry a URL or a path, across semconv versions and instrumentations. */
export const URL_ATTRIBUTE_KEYS = ["http.target", "http.route", "http.url", "url.full", "url.path"] as const

export const ORG_SLUG_PLACEHOLDER = "[orgSlug]"
export const ID_PLACEHOLDER = "[id]"

/** The path segment that introduces an org slug: `/o/<slug>/…`. */
const ORG_SEGMENT = "o"

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const DIGITS = /^\d+$/
/** A long hex run: object ids, content hashes. */
const HEX_ID = /^[0-9a-f]{16,}$/i
/**
 * A prefixed vendor id (`user_2x…`, `org_…`, `sess_…`, `cus_…`, `sub_…`):
 * a short lowercase prefix, one underscore, and a tail that holds a digit, so
 * a word like `model_selection` is not mistaken for one.
 */
const PREFIXED_ID = /^[a-z]{2,8}_(?=[A-Za-z0-9]*\d)[A-Za-z0-9]{8,}$/

const isIdSegment = (segment: string): boolean =>
  UUID.test(segment) || DIGITS.test(segment) || HEX_ID.test(segment) || PREFIXED_ID.test(segment)

const templatePath = (path: string): string => {
  const segments = path.split("/")
  return segments
    .map((segment, i) => {
      if (segment === "" || segment.startsWith("[")) return segment
      if (i > 0 && segments[i - 1] === ORG_SEGMENT) return ORG_SLUG_PLACEHOLDER
      return isIdSegment(segment) ? ID_PLACEHOLDER : segment
    })
    .join("/")
}

/**
 * The route template of a path or an absolute URL. Pure; a value that already
 * is a template (or holds no id) comes back unchanged, so the function is
 * idempotent and `routeTemplateOf(v) === v` is the "already a template" test.
 */
export const routeTemplateOf = (url: string): string => {
  const scheme = /^[a-z][a-z0-9+.-]*:\/\//i.exec(url)
  const pathStart = scheme === null ? 0 : url.indexOf("/", scheme[0].length)
  if (pathStart === -1) return url
  const rest = url.slice(pathStart)
  const tailAt = rest.search(/[?#]/)
  const path = tailAt === -1 ? rest : rest.slice(0, tailAt)
  const tail = tailAt === -1 ? "" : rest.slice(tailAt)
  const templated = templatePath(path)
  return templated === path ? url : `${url.slice(0, pathStart)}${templated}${tail}`
}
